import { useEffect, useRef } from "react";
import { Box, useTheme } from "@mui/material";

/**
 * Cloudflare Turnstile — «не робот» без ужасного CAPTCHA-опыта.
 *
 * Активируется только при наличии `REACT_APP_TURNSTILE_SITE_KEY` в .env.
 * Иначе виджет не рендерится и родительский компонент не должен
 * ожидать токен (`sendEmailCode` пропустит запрос без него).
 *
 * Скрипт `challenges.cloudflare.com/turnstile/v0/api.js` подгружается
 * лениво один раз, после чего вызовы `window.turnstile.render()`
 * рисуют встраиваемый iframe. При успехе Cloudflare даёт токен —
 * прокидываем его вверх через `onVerify`.
 */

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY?.trim() ?? "";

export const TURNSTILE_ENABLED = SITE_KEY.length > 0;

interface Props {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
}

interface TurnstileAPI {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      theme?: "light" | "dark" | "auto";
      size?: "normal" | "flexible" | "compact";
      callback?: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
    },
  ) => string; // widget id
  remove: (widgetId: string) => void;
  reset: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileAPI;
  }
}

let scriptLoading: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptLoading) return scriptLoading;

  scriptLoading = new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("turnstile script load failed"));
    document.head.appendChild(s);
  });
  return scriptLoading;
}

export function TurnstileWidget({ onVerify, onExpire, onError }: Props) {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!TURNSTILE_ENABLED) return;
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          theme: theme.palette.mode === "dark" ? "dark" : "light",
          // flexible = адаптируется под ширину контейнера
          size: "flexible",
          callback: (token) => onVerify(token),
          "expired-callback": () => onExpire?.(),
          "error-callback": () => onError?.(),
        });
      })
      .catch((err) => {
        console.warn("[turnstile] load failed", err);
        // Fail-open на клиенте: не показываем виджет, отправляем без
        // токена, бэкенд отклонит если проверка обязательна.
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
      }
    };
    // Намеренно пустые зависимости: виджет монтируем один раз на всю
    // жизнь компонента. Ре-рендер темы Turnstile не переваривает,
    // поэтому смена цветов при переключении светлой/тёмной темы —
    // за счёт auto-detection у CF.
  }, []);

  if (!TURNSTILE_ENABLED) return null;

  return (
    <Box
      ref={containerRef}
      sx={{ display: "flex", justifyContent: "center", minHeight: 65 }}
    />
  );
}
